import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

export default function ExpertInsights({
                                               contextPath = "",
                                               searchPath,
                                               mode = "profiler",
                                               defaultOrder = "DATE",
                                               height = 575,
                                           }) {
    const [keyword, setKeyword] = useState("");
    const [orderBy, setOrderBy] = useState(defaultOrder);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [html, setHtml] = useState("");
    const [items, setItems] = useState([]);

    const inputRef = useRef(null);

    const effectiveSearchPath = useMemo(() => {
        if (searchPath && searchPath.trim()) return searchPath.trim();
        return `${contextPath || ""}/industry-commentary/search`;
    }, [searchPath, contextPath]);

    const buildDownloadUrl = (id) =>
        `${contextPath || ""}/OpenIndustryCommentary?id=${encodeURIComponent(id)}`;

    const filenameFromContentDisposition = (cd) => {
        if (!cd) return null;
        const mStar = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cd);
        if (mStar) return decodeURIComponent(mStar[1]);
        const m = /filename\s*=\s*"?([^\";]+)"?/i.exec(cd);
        return m ? m[1] : null;
    };

    const formatISODateToDDMMYYYY = (value) => {
        if (!value) return "";
        const iso = String(value).slice(0, 10); // keep only the date part
        const [y, m, d] = iso.split("-");
        if (y && m && d) return `${d.padStart(2,"0")}/${m.padStart(2,"0")}/${y}`;
        return "";
    };

    const handleDownload = async (it) => {
        const url = buildDownloadUrl(it.id);
        const token = localStorage.getItem("token");

        try {
            await axios.post(
                `${contextPath || ""}/industry-commentary/log`,
                { id: it.id,  mode,  },
                { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
            );
        } catch {}

        const res = await axios.get(url, {
            responseType: "blob",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const contentDisp = res.headers?.["content-disposition"];
        const suggested =
            filenameFromContentDisposition(contentDisp) ||
            (it.fileName ? it.fileName.replace(/[\\/:*?"<>|]/g, "_") : "industry-commentary");

        const blobUrl = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = suggested;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
    };

    async function runSearch(nextOrder = orderBy, nextKeyword = keyword) {
        setLoading(true);
        setError("");

        try {
            const params = {
                keyword: (nextKeyword || "").trim(),
                dateName: nextOrder,
                mode,
                orderBy: nextOrder,
            };

            const token = localStorage.getItem("token");

            const res = await axios.post(
                effectiveSearchPath,
                null,
                {
                    params,
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                }
            );

            const data = res?.data;

            if (data && Array.isArray(data.items)) { setItems(data.items); setHtml(""); }
            else if (Array.isArray(data)) { setItems(data); setHtml(""); }
            else { setItems([]); setHtml(""); }
        } catch (e) {
            console.error("IndustryCommentary search error", e);
            setError("Failed to load Expert Insights. Please try again.");
            setItems([]);
            setHtml("");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        runSearch();
        const onKeyDown = (ev) => {
            if (ev.key === "/" && !ev.metaKey && !ev.ctrlKey && !ev.altKey) {
                ev.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [mode, effectiveSearchPath]);

    const onSubmit = (e) => {
        e.preventDefault();
        runSearch();
    };


    return (
        <div className="w-full p-4">
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">Expert Insights</h1>

            <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
                <div className="flex-1 min-w-[220px]">
                    <input
                        ref={inputRef}
                        type="text"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        placeholder="Keyword Search"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-700 font-medium">Order By</span>
                    <label className="inline-flex items-center gap-1">
                        <input
                            type="radio"
                            name="order"
                            value="DATE"
                            checked={orderBy === "DATE"}
                            onChange={() => { setOrderBy("DATE"); runSearch("DATE"); }}
                        />
                        <span>Date</span>
                    </label>
                    <label className="inline-flex items-center gap-1">
                        <input
                            type="radio"
                            name="order"
                            value="NAME"
                            checked={orderBy === "NAME"}
                            onChange={() => { setOrderBy("NAME"); runSearch("NAME"); }}
                        />
                        <span>Name</span>
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="submit"
                        className="px-4 py-2 bg-[#25408f] text-white rounded-2xl font-semibold shadow-md
                   hover:bg-[#1f367a] active:bg-[#1e3372] active:scale-95 transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-[#465da0] focus:ring-offset-2
                   border border-[#25408f] hover:border-[#1e3372] active:border-[#1c306b]
                   disabled:bg-[#25408f] disabled:border-[#25408f] disabled:opacity-60
                   disabled:cursor-not-allowed flex items-center gap-2"
                        disabled={loading}
                        title="Search"
                    >
                        {loading ? "Searching…" : "Search"}
                    </button>
                </div>
            </form>

            {error && (
                <div className="mt-3 text-sm text-red-600">{error}</div>
            )}

            <hr className="my-4" />

            <div
                className="w-full overflow-auto rounded-md border border-gray-200 bg-white"
                style={{ height }}
            >
                {html && (
                    <div
                        className="p-3 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                )}

                {!html && Array.isArray(items) && items.length > 0 && (
                  <ul className="divide-y divide-gray-100">
                      {items.map((it) => {
                        const key = it.id || it._id || `${it.fileName}-${it.date}`;
                        const title = it.fileName || "Untitled";
                        const display = title;
                        return (
                           <li key={key} className="p-3 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                              <a
                                href={buildDownloadUrl(it.id)}
                                onClick={(e) => { e.preventDefault(); handleDownload(it); }}
                                className="font-medium text-blue-700 hover:underline"
                              >
                                {display}
                              </a>
                              {it.date && (
                                  <time className="text-xs text-gray-500">
                                       {formatISODateToDDMMYYYY(it.date)}
                                     </time>
                                  )}
                             </div>
                             {it.description && (
                              <p className="mt-1 text-sm text-gray-700">{it.description}</p>
                            )}
                         </li>
                       );
                      })}
                      </ul>
                )}

                {!html && Array.isArray(items) && items.length === 0 && !loading && !error && (
                    <div className="p-3 text-sm text-gray-500">No results.</div>
                )}

                {loading && (
                    <div className="p-3 text-sm text-gray-600">Loading…</div>
                )}
            </div>

        </div>
    );
}
